import { useEffect, useRef, useState, type SVGProps } from "react";
import { useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  CalendarCheck,
  Check,
  ChevronLeft,
  ChevronRight,
  Dumbbell,
  HeartHandshake,
  BellRing,
  BookOpen,
  Bookmark,
  Briefcase,
  Flag,
  GraduationCap,
  History,
  UserCheck,
  MessageSquareText,
  MoreHorizontal,
  PenTool,
  Paperclip,
  Pause,
  PawPrint,
  Phone,
  Play,
  RotateCcw,
  Search,
  Send,
  Smile,
  Sparkles,
  Video,
  type LucideIcon,
} from "lucide-react";
import HeroFeatures from "./HeroFeatures";
import HeroDashboard from "./HeroDashboard";
import IntroducingDibs from "./IntroducingDibs";
import JourneyShowcase from "./JourneyShowcase";
import HowDibsWorks from "./HowDibsWorks";
import ProgressSection from "./ProgressSection";
import { useSectionEmphasis } from "./useSectionEmphasis";
import "./landing-v2.css";
import "./landing-page.css";

const SHOW_JOURNEY_SHOWCASE = false;

const TRIAL_URL = "/auth?source=free_trial";
const FOUNDER_URL = "https://wa.me/6587169250";

const outcomes: Array<{
  title: string;
  description: string;
  graphic: "trust" | "control" | "languages";
}> = [
  {
    title: "Human-approved to autopilot",
    description:
      "Review every message during a one-month pilot. Once Dibs has earned your trust, it runs on its own.",
    graphic: "trust",
  },
  {
    title: "Only pings you when it matters",
    description:
      "Dibs handles the routine on its own and only comes to you for what needs a human, like a refund or a complaint. The rest never touches your inbox.",
    graphic: "control",
  },
  {
    title: "Handles every language for you",
    description:
      "Chat in Chinese, Malay or Tamil. Dibs understands and replies, so you never have to translate.",
    graphic: "languages",
  },
];

function OutcomeGraphic({ type }: { type: "trust" | "control" | "languages" }) {
  if (type === "trust") {
    return (
      <div className="dh-outcome-graphic dh-trust" aria-hidden>
        <div className="dh-trust-track"><span className="dh-trust-fill" /></div>
        <div className="dh-trust-steps">
          <span><b>Month 1</b>You review every message</span>
          <span className="is-final"><b>Month 2+</b>Dibs runs on its own</span>
        </div>
      </div>
    );
  }
  if (type === "control") {
    return (
      <div className="dh-outcome-graphic dh-control" aria-hidden>
        <span className="dh-control-row"><b>Booking confirmation</b><i>Handled</i></span>
        <span className="dh-control-row is-flagged"><b>Refund request</b><i>Needs you</i></span>
        <span className="dh-control-row"><b>Appointment reminder</b><i>Handled</i></span>
        <span className="dh-control-summary">2 handled, 1 needs you</span>
      </div>
    );
  }
  return (
    <div className="dh-outcome-graphic dh-languages" aria-hidden>
      <span className="dh-lang-bubble">你好，请问可以预约吗？</span>
      <span className="dh-lang-bubble">Boleh saya tempah janji temu?</span>
      <span className="dh-lang-bubble">நான் முன்பதிவு செய்யலாமா?</span>
    </div>
  );
}

const faqs = [
  {
    category: "start",
    question: "What does Dibs help my business do?",
    answer:
      "Dibs brings customer information, conversations, and follow-ups together, helping your team keep context and spend less time coordinating repeat work.",
  },
  {
    category: "start",
    question: "How is Dibs different from traditional CRM systems?",
    answer:
      "Traditional CRMs are often built around records your team has to maintain. Dibs connects the context you already create across conversations and customer activity, then turns it into a clear next action.",
  },
  {
    category: "control",
    question:
      "Why is Dibs' automations more effective than most competitors out there?",
    answer:
      "Dibs uses the relationship behind the task — not just a timer or status change. That means follow-ups can reflect a customer's history, preferences and recent activity, with human review whenever you want it.",
  },
  {
    category: "control",
    question: "What platforms and channels does Dibs support?",
    answer:
      "Dibs brings customer context together across the channels businesses use most, including WhatsApp and Instagram, with more connections available as your setup grows.",
  },
  {
    category: "plans",
    question: "How are monthly engaged customers counted?",
    answer:
      "Your monthly engaged-customer allowance is separate from inactive customers saved in Dibs. We’ll walk through which interactions count and how the usage period works during your demo.",
  },
  {
    category: "plans",
    question: "What happens if I need to engage with more customers?",
    answer:
      "You can add packs of 100 monthly engaged customers: Starter at S$90 per month (up to 500 in total), Growth at S$70 (up to 1,200) and Premium at S$60 (up to 12,000). Enterprise capacity is agreed individually.",
  },
  {
    category: "control",
    question: "Can my team approve messages before they are sent?",
    answer:
      "Yes. You can keep a human in the loop, review the suggested wording and decide what goes out. Dibs helps your team move faster without taking control away.",
  },
  {
    category: "start",
    question: "How quickly can we get started?",
    answer:
      "Most teams can begin with their existing customer information and one high-value workflow. We will help you choose a focused first use case during your demo.",
  },
  {
    category: "start",
    question: "What happens during a demo?",
    answer:
      "See how Dibs works and discuss your customer journey, requirements, and selected plan with us.",
  },
];

const faqTabs = [
  { id: "all", label: "All questions" },
  { id: "start", label: "Getting started" },
  { id: "plans", label: "Plans & usage" },
  { id: "control", label: "Control & channels" },
] as const;

const innovationPartners = [
  { id: "meta", src: "/dibs_assets/meta-logo.webp", name: "Meta Official Business Partner" },
  { id: "nvidia", src: "/dibs_assets/nvidia-logo.svg", name: "NVIDIA Inception Program" },
  { id: "stripe", src: "/dibs_assets/stripe-logo.webp", name: "Stripe for Startups Program" },
  { id: "twilio", src: "/dibs_assets/twilio-logo.svg", name: "Official Twilio Solutions Partner" },
];

const customerLogos = [
  { id: "bit-by-bit", src: "/partners/bit-by-bit-coding.png", alt: "Bit by Bit Coding", name: "Bit by Bit Coding" },
  { id: "naga", src: "/partners/naga-fitness.png", alt: "Naga Fitness" },
  { id: "p0poff", src: "/partners/p0poff.PNG", alt: "P0poff", name: "p0poff" },
];

function ProductPreview() {
  return (
    <div className="dh-product" aria-label="Illustration of the Dibs customer workspace">
      <div className="dh-product-bar">
        <div className="dh-product-brand">
          <img src="/dibs_assets/red_logo_with_transparent_bg.png" alt="" />
          <span>Dibs</span>
        </div>
        <div className="dh-product-status"><span /> All changes saved</div>
      </div>

      <div className="dh-product-grid">
        <aside className="dh-customer-list" aria-label="Sample customers">
          <p className="dh-ui-label">Customers</p>
          <div className="dh-search"><Search aria-hidden className="dh-search-icon" /> Search</div>
          <button type="button" className="dh-customer is-active">
            <span className="dh-avatar dh-avatar-coral">AT</span>
            <span><strong>Avery Tan</strong><small>WhatsApp · now</small></span>
          </button>
          <button type="button" className="dh-customer">
            <span className="dh-avatar dh-avatar-violet">JL</span>
            <span><strong>Jordan Lim</strong><small>Instagram · 2h</small></span>
          </button>
          <button type="button" className="dh-customer">
            <span className="dh-avatar dh-avatar-sage">SL</span>
            <span><strong>Sofia Lee</strong><small>WhatsApp · 1d</small></span>
          </button>
        </aside>

        <div className="dh-customer-profile">
          <div className="dh-profile-header">
            <span className="dh-avatar dh-avatar-coral dh-avatar-lg">AT</span>
            <div><p className="dh-ui-label">Customer profile</p><h2>Avery Tan</h2></div>
            <span className="dh-status-badge">VIP customer</span>
          </div>

          <div className="dh-context-row">
            <span>Prefers weekday evenings</span>
            <span>8 completed bookings</span>
            <span>Usually replies on WhatsApp</span>
          </div>

          <div className="dh-next-step">
            <div className="dh-next-step-title">
              <span className="dh-icon-box"><Sparkles aria-hidden /></span>
              <div><small>Recommended next step</small><strong>Thank Avery for the referral</strong></div>
            </div>
            <p>Avery referred a new customer yesterday. Offer their usual Thursday evening slot in the same message.</p>
            <button type="button">Prepare follow-up <ArrowRight aria-hidden /></button>
          </div>

          <div className="dh-activity">
            <div><span className="dh-activity-icon"><MessageSquareText aria-hidden /></span><p><strong>Conversation updated</strong><small>“Thursday evening works perfectly.”</small></p><time>Today</time></div>
            <div><span className="dh-activity-icon"><CalendarCheck aria-hidden /></span><p><strong>Booking completed</strong><small>Monthly appointment · 60 minutes</small></p><time>4 days ago</time></div>
          </div>
        </div>

        <aside className="dh-insight-panel">
          <p className="dh-ui-label">Relationship pulse</p>
          <div className="dh-score"><strong>92</strong><span>Strong</span></div>
          <div className="dh-score-line"><i /></div>
          <dl>
            <div><dt>Lifetime value</dt><dd>S$840</dd></div>
            <div><dt>Last visit</dt><dd>4 days ago</dd></div>
            <div><dt>Next booking</dt><dd>Not booked</dd></div>
          </dl>
          <div className="dh-insight-note"><Sparkles aria-hidden /> A thoughtful follow-up is ready.</div>
        </aside>
      </div>
    </div>
  );
}

function CredibilitySection() {
  return (
    <section className="dh-logo-section" aria-label="Partners and customers">
      <div className="dh-container dh-logo-layout">
        <div className="dh-logo-group dh-innovation-group" aria-labelledby="dh-innovation-partners-title">
          <h2 id="dh-innovation-partners-title">Innovation partners</h2>
          <ul className="dh-innovation-logos">
            {innovationPartners.map((partner) => (
              <li className="dh-innovation-partner" key={partner.id}>
                <span className={`dh-innovation-mark dh-innovation-mark--${partner.id}`}>
                  <img src={partner.src} alt="" loading="lazy" decoding="async" />
                </span>
                <span>{partner.name}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="dh-logo-group dh-customer-group" aria-labelledby="dh-customer-logos-title">
          <h2 id="dh-customer-logos-title">Trusted by teams growing through better relationships</h2>
          <div className="dh-customer-marquee" role="region" aria-label="Customer logos">
            <div className="dh-customer-track">
              {[0, 1, 2, 3].map((copy) => (
                <ul className="dh-customer-logos" key={copy} aria-hidden={copy > 0 ? true : undefined}>
                  {customerLogos.map((logo) => (
                    <li className="dh-customer-logo" key={logo.id}>
                      <img className={`dh-customer-logo--${logo.id}`} src={logo.src} alt={copy === 0 && !logo.name ? logo.alt : ""} loading="lazy" decoding="async" />
                      {logo.name && <span className="dh-customer-logo-name">{logo.name}</span>}
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const ToothIcon = ((props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M7 3c-2.5 0-4 2-4 4.5 0 2.2 1 3.6 1.6 5.6.6 2 .7 4.4 1.6 6.6.5 1.2 1.8 1.3 2.3.2.8-1.7.8-4.2 2.5-4.2s1.7 2.5 2.5 4.2c.5 1.1 1.8 1 2.3-.2.9-2.2 1-4.6 1.6-6.6.6-2 1.6-3.4 1.6-5.6C21 5 19.5 3 17 3c-1.8 0-3 .9-5 .9S8.8 3 7 3Z" />
  </svg>
)) as unknown as LucideIcon;

const industries: Array<{ id: string; name: string; icon: LucideIcon; tone: string; image: string; description: string; solutions: string[] }> = [
  {
    id: "dental",
    name: "Dental clinics",
    icon: ToothIcon,
    tone: "linear-gradient(160deg, hsl(14 80% 52%), hsl(350 62% 24%))",
    image: "/dibs_assets/industries/dental.jpg",
    description: "Fill the chair and keep patients coming back. Dibs handles booking requests, recall reminders, and post-visit follow-ups across WhatsApp and Instagram.",
    solutions: ["Booking from chat", "Recall reminders", "Treatment follow-ups", "Patient history in one place"],
  },
  {
    id: "agencies",
    name: "Agencies",
    icon: PenTool,
    tone: "linear-gradient(160deg, hsl(222 30% 38%), hsl(222 47% 11%))",
    image: "/dibs_assets/industries/agencies.jpg",
    description: "Keep every client conversation and brief in one place. Dibs triages new enquiries, answers routine questions, and flags the accounts that need a personal touch.",
    solutions: ["Lead qualification", "Client request tracking", "Renewal nudges", "Team handoffs"],
  },
  {
    id: "pet-grooming",
    name: "Pet grooming studios",
    icon: PawPrint,
    tone: "linear-gradient(160deg, hsl(32 85% 50%), hsl(14 60% 24%))",
    image: "/dibs_assets/industries/pet-grooming.jpg",
    description: "Keep the schedule full and pet parents happy. Dibs books grooming slots, remembers each pet's preferences, and reminds owners when the next groom is due.",
    solutions: ["Slot booking", "Pet profiles and notes", "Rebooking reminders", "Care instructions"],
  },
  {
    id: "fitness",
    name: "Gyms & fitness",
    icon: Dumbbell,
    tone: "linear-gradient(160deg, hsl(350 78% 52%), hsl(330 45% 20%))",
    image: "/dibs_assets/industries/fitness.jpg",
    description: "Turn trials into members and keep members training. Dibs answers class questions, books trial sessions, and re-engages members who have gone quiet.",
    solutions: ["Trial class booking", "Membership questions", "Lapsed-member win-back", "Class reminders"],
  },
  {
    id: "nonprofit",
    name: "Non-profit organisations",
    icon: HeartHandshake,
    tone: "linear-gradient(160deg, hsl(160 38% 36%), hsl(180 40% 13%))",
    image: "/dibs_assets/industries/nonprofit.jpg",
    description: "Keep every beneficiary's story, even when staff move on. Dibs holds case notes, needs and next steps in one place, so any caseworker can pick up with context, while donors and volunteers still get a personal reply.",
    solutions: ["Beneficiary case history", "Smooth staff handovers", "Donor thank-yous", "Volunteer coordination"],
  },
];

function IndustriesSection() {
  const [active, setActive] = useState(0);
  const current = industries[active];
  const step = (delta: number) => setActive((index) => (index + delta + industries.length) % industries.length);

  return (
    <section className="dh-section dh-industries" aria-labelledby="dh-industries-title">
      <div className="dh-container">
        <div className="dh-section-heading">
          <div className="dh-kicker">Who Dibs is for</div>
          <h2 id="dh-industries-title">One platform, tuned to how your industry works</h2>
          <p>The same context and follow-up engine, shaped around the way your team actually talks to the people it serves.</p>
        </div>

        <div className="dh-industry-cards">
          {industries.map((industry, index) => {
            const Icon = industry.icon;
            return (
              <button
                key={industry.id}
                type="button"
                className={`dh-industry-card${index === active ? " is-active" : ""}`}
                style={{ backgroundImage: industry.tone }}
                aria-pressed={index === active}
                onClick={() => setActive(index)}
              >
                <img className="dh-industry-photo" src={industry.image} alt="" loading="lazy" decoding="async" />
                <span className="dh-industry-tint" style={{ backgroundImage: industry.tone }} aria-hidden />
                <Icon className="dh-industry-icon" aria-hidden />
                <span className="dh-industry-name">{industry.name}</span>
              </button>
            );
          })}
        </div>

        <div className="dh-industry-detail" aria-live="polite">
          <p className="dh-industry-description" key={current.id}>{current.description}</p>
          <div className="dh-industry-solutions" key={`${current.id}-solutions`}>
            <h3>Our solutions</h3>
            <ul>
              {current.solutions.map((solution) => <li key={solution}>{solution}</li>)}
            </ul>
          </div>
          <div className="dh-industry-nav">
            <button type="button" onClick={() => step(-1)} aria-label="Previous industry"><ChevronLeft aria-hidden /></button>
            <button type="button" onClick={() => step(1)} aria-label="Next industry"><ChevronRight aria-hidden /></button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  const [faqTab, setFaqTab] = useState<(typeof faqTabs)[number]["id"]>("all");
  const visibleFaqs = faqs.filter((faq) => faqTab === "all" || faq.category === faqTab);
  const introRef = useRef<HTMLDivElement>(null);
  useSectionEmphasis(introRef);
  const journeyRef = useRef<HTMLDivElement>(null);
  useSectionEmphasis(journeyRef);

  return (
    <main id="main-content" className="dibs-home">
      <HeroDashboard><HeroFeatures /></HeroDashboard>

      <CredibilitySection />

      <section className="dh-pain-points" aria-labelledby="dh-pain-points-title">
        <div className="dh-container">
          <div className="dh-pain-intro">
            <div className="dh-pain-copy">
              <div className="dh-kicker">The cost of forgetting</div>
              <h2 id="dh-pain-points-title">People expect to be known. Can your team remember everyone?</h2>
            </div>

            <div className="dh-pain-stat" aria-label="76 percent of consumers get frustrated when companies do not personalize interactions">
              <strong>76%</strong>
              <p>of consumers get frustrated when a company doesn’t personalize their interactions.</p>
            </div>
          </div>

          <div className="dh-pain-grid">
            <article className="dh-pain-card">
              <span aria-hidden="true">01</span>
              <h3>Not across apps.</h3>
              <p>Someone messages on Instagram, then WhatsApp, and your team treats each chat as a first conversation.</p>
            </article>
            <article className="dh-pain-card">
              <span aria-hidden="true">02</span>
              <h3>Not when staff change.</h3>
              <p>The history, preferences and promises go with whoever moves on.</p>
            </article>
            <article className="dh-pain-card">
              <span aria-hidden="true">03</span>
              <h3>Not at the right moment.</h3>
              <p>The birthday, the regular who went quiet and the missed feedback are noticed only after the moment has passed.</p>
            </article>
          </div>

          <p className="dh-pain-source">
            Source:{" "}
            <a
              href="https://www.mckinsey.com/capabilities/growth-marketing-and-sales/our-insights/the-value-of-getting-personalization-right-or-wrong-is-multiplying"
              target="_blank"
              rel="noopener noreferrer"
            >
              McKinsey, “The value of getting personalization right, or wrong, is multiplying”, 2021
            </a>
          </p>
        </div>
      </section>

      <div ref={introRef} className="dibs-v2">
        <IntroducingDibs />
      </div>

      <IndustriesSection />

      <HowDibsWorks />

      {/* Personalise customer journeys section hidden for now. */}
      {SHOW_JOURNEY_SHOWCASE && <div ref={journeyRef} className="dibs-v2">
        <JourneyShowcase />
      </div>}

      <section className="dh-section dh-outcomes" aria-labelledby="dh-outcomes-title">
        <div className="dh-container">
          <div className="dh-section-heading">
            <div className="dh-kicker">How Dibs fits in</div>
            <h2 id="dh-outcomes-title">We want you to use Dibs less.</h2>
            <p>Dibs drafts, follows up and books in the background, so you spend your time with people, not in another tool.</p>
          </div>
          <div className="dh-outcome-grid">
            {outcomes.map(({ graphic, title, description }) => (
              <article key={title} className="dh-outcome-card">
                                <OutcomeGraphic type={graphic} />
                <h3>{title}</h3><p>{description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Hidden: "One connected workspace" spotlight
      <section className="dh-section dh-spotlight" aria-labelledby="dh-spotlight-title">
        <div className="dh-container dh-spotlight-grid">
          <div className="dh-spotlight-copy">
            <div className="dh-kicker">One connected workspace</div>
            <h2 id="dh-spotlight-title">Every signal has somewhere useful to go.</h2>
            <p>Conversations become context. Context becomes the next best action. Every action makes the next conversation more personal.</p>
            <ul>
              <li><Check aria-hidden /> Bring customer conversations into one view</li>
              <li><Check aria-hidden /> Spot rebooking and retention opportunities</li>
              <li><Check aria-hidden /> Keep your team aligned on what happens next</li>
            </ul>
            <a href={TRIAL_URL}>Explore Dibs <ArrowRight aria-hidden /></a>
          </div>
          <div className="dh-loop-card" aria-label="Dibs relationship loop">
            <div className="dh-loop-orbit" />
            <div className="dh-loop-center"><img src="/dibs_assets/red_logo_with_transparent_bg.png" alt="" /><strong>Dibs</strong><span>Relationship context</span></div>
            <div className="dh-loop-node dh-loop-node-one"><MessageSquareText aria-hidden /><span><small>Listen</small>Conversations</span></div>
            <div className="dh-loop-node dh-loop-node-two"><Sparkles aria-hidden /><span><small>Understand</small>Customer context</span></div>
            <div className="dh-loop-node dh-loop-node-three"><CalendarCheck aria-hidden /><span><small>Act</small>Next best step</span></div>
          </div>
        </div>
      </section>
      */}

      <ProgressSection />

      <section id="faq" className="dh-section dh-faq" aria-labelledby="dh-faq-title">
        <div className="dh-container dh-faq-grid">
          <div className="dh-faq-intro">
            <div className="dh-kicker">Questions, answered</div>
            <h2 id="dh-faq-title">A few things you might be wondering.</h2>
            <p>Want to talk through your setup? We will help you find the first use case worth solving.</p>
            <a className="dh-button dh-button-secondary" href={FOUNDER_URL} target="_blank" rel="noopener noreferrer">Book a demo <ArrowUpRight aria-hidden /></a>
          </div>
          <div className="dh-faq-panel">
          <div className="dh-faq-tabs" role="tablist" aria-label="FAQ categories">
            {faqTabs.map((tab) => (
              <button key={tab.id} type="button" role="tab" aria-selected={faqTab === tab.id} className="dh-faq-tab" onClick={() => setFaqTab(tab.id)}>{tab.label}</button>
            ))}
          </div>
          <div className="dh-faq-list">
            {visibleFaqs.map((faq, index) => (
              <details key={`${faqTab}-${faq.question}`} open={index === 0}>
                <summary><span>{faq.question}</span><span className="dh-faq-control"><ChevronRight aria-hidden /></span></summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
          </div>
        </div>
      </section>

      <section className="dh-closing" aria-labelledby="dh-closing-title">
        <div className="dh-container dh-closing-content">
          <h2 id="dh-closing-title">Every customer remembered.<br />Without the extra work.</h2>
          <p>Dibs remembers every conversation and follows up in the background, so your team spends its time with people.</p>
          <div className="dh-hero-actions">
            <a className="dh-button dh-button-primary" href={FOUNDER_URL} target="_blank" rel="noopener noreferrer">Book a demo <ArrowUpRight aria-hidden /></a>
          </div>
        </div>
      </section>
    </main>
  );
}
